import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { phaseMeta, timelineEvents, type TimelineEvent } from "@/mocks/siteData";

const phases = ["全部", "发酵", "追问", "反转", "回应"] as const;
type PhaseFilter = (typeof phases)[number];

export default function Timeline() {
  const [filter, setFilter] = useState<PhaseFilter>("全部");
  const [expanded, setExpanded] = useState<string[]>(timelineEvents.map((t) => t.id));

  const shown = useMemo(
    () => (filter === "全部" ? timelineEvents : timelineEvents.filter((t) => t.phase === filter)),
    [filter]
  );

  const toggle = (id: string) => {
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const lastIdx = shown.length - 1;

  return (
    <section id="timeline" className="relative scroll-mt-20 overflow-hidden bg-background-950 py-16 text-background-50 md:py-24">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(oklch(var(--accent-500) / 0.9) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            dark
            kicker="Full Timeline"
            title="完整时间线 · 按顺序追完全程"
            subtitle="从开播到现在，每个关键节点都标好日期与阶段。倒回看也不迷路，这就是追更的正确打开方式。"
          />
          <div className="inline-flex w-max flex-wrap items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1">
            {phases.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setFilter(p)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors md:px-4 ${
                  filter === p
                    ? "bg-accent-500 text-background-50"
                    : "text-background-200/80 hover:bg-white/10 hover:text-background-50"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {shown.length === 0 ? (
            <p className="rounded-xl border border-white/10 bg-white/5 px-5 py-10 text-center text-sm text-background-200/70">
              该阶段暂无可展示的节点，可切换到其它阶段查看。
            </p>
          ) : (
            <ol>
              {shown.map((event: TimelineEvent, i: number) => {
                const meta = phaseMeta[event.phase];
                const isOpen = expanded.includes(event.id);
                return (
                  <li key={event.id} className="flex gap-4 md:gap-6">
                    <div className="flex w-14 shrink-0 flex-col items-center md:w-16">
                      <span className={`w-0.5 flex-1 ${i === 0 ? "bg-transparent" : "bg-white/15"}`} aria-hidden="true" />
                      <span className={`my-1 inline-flex h-4 w-4 shrink-0 rounded-full ring-4 ring-background-950 ${meta.dot}`} />
                      <span className={`w-0.5 flex-1 ${i === lastIdx ? "bg-transparent" : "bg-white/15"}`} aria-hidden="true" />
                    </div>

                    <div className="min-w-0 flex-1 pb-8">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading text-sm font-black text-accent-300">{event.date}</span>
                        <span className="rounded bg-white/10 px-2 py-0.5 text-[11px] text-background-200/80">{event.day}</span>
                        <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${meta.label}`}>{event.phase}</span>
                      </div>
                      <h3 className="mt-2 font-heading text-lg font-bold text-background-50 md:text-xl">
                        {event.title}
                      </h3>
                      {isOpen ? (
                        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-background-200/85">{event.body}</p>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => toggle(event.id)}
                        aria-expanded={isOpen}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-md text-xs font-semibold text-accent-300 hover:text-accent-200"
                      >
                        {isOpen ? (
                          <>
                            <i className="ri-subtract-line" />
                            收起
                          </>
                        ) : (
                          <>
                            <i className="ri-add-line" />
                            展开这一节点
                          </>
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-accent-400/30 bg-accent-500/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-accent-100">
            <strong className="font-black text-accent-300">下一节点预告：</strong>
            平台「阶段结论」将于约 <strong className="font-black">48 小时</strong>内给出，倒计时结束自动并入时间线更新。
          </p>
          <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-bold text-accent-300">
            <i className="ri-timer-flash-line text-base" />
            蹲更新中，别走开
          </span>
        </div>
      </div>
    </section>
  );
}